import { EDecision } from '@if/core/types'
import { ActionButton, BodyText, DialogContentSkeleton, type DialogContentContext, type Tone, useDialog } from '@if/mui'
import { Box, Divider, List, ListItemButton, ListItemText, Stack, TextField } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { ApplicantDto } from '@src/types/CreditApplicationSchema'
import axios from 'axios'
import { every } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface TestCase {
  id: string
  applicantDecisionAuto: EDecision
  applicant: ApplicantDto
  description: string
}

/** Decision → its tone, the row bar's palette color, and its filter label. One table so the three can't disagree. */
const DECISION_STYLES = {
  [EDecision.Approved]: { tone: 'success', color: 'success.main', labelKey: 'common.testCaseApproved' },
  [EDecision.ManualRevision]: { tone: 'warning', color: 'warning.main', labelKey: 'common.testCaseReview' },
  [EDecision.Refused]: { tone: 'destructive', color: 'error.main', labelKey: 'common.testCaseDeclined' },
} as const satisfies Partial<Record<EDecision, { tone: Tone; color: string; labelKey: string }>>

/** The table is keyed by the three decisions it styles; every other decision falls through. */
const decisionStyle = (decision: EDecision) =>
  (DECISION_STYLES as Partial<Record<EDecision, (typeof DECISION_STYLES)[keyof typeof DECISION_STYLES]>>)[decision]

const FILTERABLE_DECISIONS = [EDecision.Approved, EDecision.ManualRevision, EDecision.Refused] as const

const TEST_CASES = 'creditTestCases'
const fiveMinutes = 300000
const emptyTestCases: TestCase[] = []

// Dev-only proxy; not in the app API surface, so it isn't behind `getApiClient`.
const testCasesUrl = 'https://func-if-proxies-dev-cac.azurewebsites.net/CreditTestCases'

const keysFactory = {
  testCases: () => [{ scope: TEST_CASES }] as const,
}

async function fetchTestCases(): Promise<TestCase[]> {
  const response = await axios.get<TestCase[]>(testCasesUrl)
  // The feed carries no id; index is stable within a fetched page and is all the list needs.
  return response.data.map((testcase, index) => ({ ...testcase, id: index.toString() }))
}

function useTestCasesQuery() {
  return useQuery({
    queryKey: keysFactory.testCases(),
    queryFn: fetchTestCases,
    staleTime: fiveMinutes,
    gcTime: fiveMinutes,
  })
}

/** Everything the toolbar and the body share — declared as the dialog's own state so both re-render together. */
const useTestCaseDialogState = () => {
  const { data, isPending, isError } = useTestCasesQuery()
  const [textFilter, setTextFilter] = useState('')
  const [decisionFilter, setDecisionFilter] = useState<EDecision | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const testCases = data ?? emptyTestCases

  const filteredTestCases = useMemo(() => {
    const words = textFilter.toLowerCase().split(' ')
    return testCases.filter((testcase) => {
      if (decisionFilter && testcase.applicantDecisionAuto !== decisionFilter) return false
      return every(
        words,
        (word) =>
          testcase.applicant.firstName.toLowerCase().includes(word) ||
          (testcase.applicant.lastName ?? '').toLowerCase().includes(word) ||
          testcase.description.toLowerCase().includes(word),
      )
    })
  }, [textFilter, decisionFilter, testCases])

  // A row the filter has hidden must not stay loadable — drop the selection with it.
  useEffect(() => {
    if (selectedId !== null && !filteredTestCases.some((testcase) => testcase.id === selectedId)) setSelectedId(null)
  }, [filteredTestCases, selectedId])

  const selectedTestCase = filteredTestCases.find((testcase) => testcase.id === selectedId) ?? null

  /** Click the active filter again to clear it. */
  const toggleDecisionFilter = (decision: EDecision) =>
    setDecisionFilter((current) => (current === decision ? null : decision))

  return {
    isPending,
    isError,
    textFilter,
    setTextFilter,
    decisionFilter,
    toggleDecisionFilter,
    filteredTestCases,
    selectedId,
    setSelectedId,
    selectedTestCase,
  }
}

type TestCaseDialogState = ReturnType<typeof useTestCaseDialogState>
type TestCaseDialogContext = DialogContentContext<void, ApplicantDto, TestCaseDialogState>

interface TestCaseItemProps {
  onTestCaseSelected: (id: string) => void
  testcase: TestCase
  selected: boolean
}

const TestCaseItem = ({ onTestCaseSelected, testcase, selected }: TestCaseItemProps) => {
  const { applicant } = testcase
  const primaryText = `${applicant.firstName} ${applicant.lastName} (${applicant.birthDate?.toString() ?? ''})`
  const decisionColor = decisionStyle(testcase.applicantDecisionAuto)?.color ?? 'divider'
  return (
    <>
      <ListItemButton onClick={() => onTestCaseSelected(testcase.id)} selected={selected} sx={{ gap: 1.5 }}>
        <Box sx={{ alignSelf: 'stretch', width: 4, flexShrink: 0, borderRadius: 1, bgcolor: decisionColor }} />
        <ListItemText primary={primaryText} secondary={testcase.description} />
      </ListItemButton>
      <Divider />
    </>
  )
}

/** The band above the list: free-text filter on the left, the decision filters on the right. */
const TestCaseDialogToolbar = ({ ctx }: { ctx: TestCaseDialogContext }) => {
  const { t } = useTranslation()
  const { textFilter, setTextFilter, decisionFilter, toggleDecisionFilter } = ctx.state

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent="space-between"
      spacing={2}
    >
      <TextField
        name="filter"
        size="small"
        label={t('common.filter')}
        value={textFilter}
        onChange={(e) => setTextFilter(e.target.value)}
        sx={{ width: { xs: '100%', sm: 280 } }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        {FILTERABLE_DECISIONS.map((decision) => {
          const style = DECISION_STYLES[decision]
          const active = decisionFilter === decision
          return (
            <ActionButton
              key={decision}
              tone={style.tone}
              emphasis={active ? 'principal' : 'normal'}
              aria-pressed={active}
              onClick={() => toggleDecisionFilter(decision)}
            >
              {t(style.labelKey)}
            </ActionButton>
          )
        })}
      </Stack>
    </Stack>
  )
}

const CenteredMessage = ({ message, isError }: { message: string; isError?: boolean }) => (
  <Box sx={{ p: 4, textAlign: 'center' }}>
    <BodyText color={isError ? 'error' : 'text.secondary'}>{message}</BodyText>
  </Box>
)

const TestCaseDialogContent = ({ ctx }: { ctx: TestCaseDialogContext }) => {
  const { t } = useTranslation()
  const { isPending, isError, filteredTestCases, selectedId, setSelectedId } = ctx.state

  if (isPending) return <DialogContentSkeleton />
  if (isError) return <CenteredMessage message={t('common.testCaseLoadFailed')} isError />
  if (filteredTestCases.length === 0) return <CenteredMessage message={t('common.noTestCaseMatchesFilter')} />

  return (
    <List disablePadding sx={{ bgcolor: 'background.paper' }}>
      {filteredTestCases.map((testcase) => (
        <TestCaseItem
          key={testcase.id}
          testcase={testcase}
          onTestCaseSelected={setSelectedId}
          selected={testcase.id === selectedId}
        />
      ))}
    </List>
  )
}

/** Dev-only "load a test case" picker; resolves the chosen applicant, or `null` on cancel. */
export const useTestCaseDialog = () => {
  const { t } = useTranslation()
  return useDialog<void, ApplicantDto, TestCaseDialogState>({
    title: t('common.selectTestCase'),
    confirmLabel: t('common.load'),
    cancelLabel: t('common.cancel'),
    maxWidth: 'md',
    fullWidth: true,
    edgeToEdge: true,
    useDialogState: useTestCaseDialogState,
    toolbar: (ctx) => <TestCaseDialogToolbar ctx={ctx} />,
    content: (ctx) => <TestCaseDialogContent ctx={ctx} />,
    confirmDisabled: (ctx) => !ctx.state?.selectedTestCase,
    onConfirm: (ctx) => ctx.state.selectedTestCase!.applicant,
  })
}
